import { FeatureCollection, Polygon, MultiPolygon, Point } from "geojson";

import { Deal, Job } from "../api";
import { FileDownloader } from "./downloadFile";
import { GeoData } from "@rogoag/shared-ts";

export function titleCase(str: string) {
	return str
		.toLowerCase()
		.split(" ")
		.map(function (word) {
			return word.replace(word[0], word[0]?.toUpperCase());
		})
		.join(" ");
}

// default typed local storage getter with optional default value
export function localStorageGet<T>(key: string, defaultValue: T): T | undefined {
	const localStorageRawValue = localStorage.getItem(key);
	if (localStorageRawValue && localStorageRawValue !== "") {
		return JSON.parse(localStorageRawValue) as T;
	}
	// this should only happen initially, then never again
	// this is so as a dev we can make edits to the value in unique
	// circumstances. This guarantees it exists in local storage
	//localStorageSet(key, defaultValue);
	return defaultValue;
}

// default typed local storage setter
export function localStorageSet<T>(key: string, value: T) {
	if (!value) {
		localStorage.removeItem(key);
		return;
	}
	localStorage.setItem(key, JSON.stringify(value));
}

// default typed local storage generator
export function LocalStorageGenerator<T>(
	key: string,
	defaultValue: T,
	{
		// can force this to be undefined because we are requiring our default value
		getter = (defaultValue: T) => localStorageGet<T>(key, defaultValue)!,
		setter = (value: T | undefined) => localStorageSet(key, value),
	} = {},
) {
	return {
		key,
		get: () => getter(defaultValue),
		set: setter,
		reset: () => setter(defaultValue),
	};
}

export function sleep(ms: number) {
	return new Promise<void>((r) => setTimeout(r, ms));
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 *
 * @param func The function to debounce
 * @param wait The number of milliseconds to delay
 * @returns A debounced function with a cancel method
 */
export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T & { cancel: () => void } {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	const debounced = function (this: any, ...args: Parameters<T>) {
		if (timeoutId !== null) {
			clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(() => {
			func.apply(this, args);
			timeoutId = null;
		}, wait);
	} as T & { cancel: () => void };

	debounced.cancel = () => {
		if (timeoutId !== null) {
			clearTimeout(timeoutId);
			timeoutId = null;
		}
	};

	return debounced;
}

export function getCookie(name: string): string | null {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		const cookie = parts.pop()?.split(";").shift() || null;
		return cookie ? decodeURIComponent(cookie) : null;
	} else {
		return null;
	}
}

export function setCookie(name: string, value: string, days: number) {
	const expires = new Date(Date.now() + days * 864e5).toUTCString();
	document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

// Short duration JWT token (5-10 min)
export function getJwtToken() {
	return localStorage.getItem("jwt");
}

export function setJwtToken(token: string) {
	localStorage.setItem("jwt", token);
}

// Longer duration refresh token (30-60 min)
export function getRefreshToken() {
	return localStorage.getItem("refreshToken");
}

export function setRefreshToken(token: string) {
	localStorage.setItem("refreshToken", token);
}

export namespace GeoJSON {
	export async function getBoundary(job: Job) {
		if (job.attachments.boundaryGeoJSON && job.attachments.boundaryGeoJSON[0]) {
			const bndGeoJSON = await FileDownloader.loadAttachment<string>(
				job.attachments.boundaryGeoJSON[0],
				job.id,
				"json",
			);
			if (bndGeoJSON) {
				return bndGeoJSON as unknown as FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>;
			}
		}

		if (job.attachments.boundarySHP && job.attachments.boundarySHP[0]) {
			const bndShp = await FileDownloader.loadAttachment<ArrayBuffer>(
				job.attachments.boundarySHP[0],
				job.id,
				"arraybuffer",
			);
			if (bndShp) {
				const shp = Buffer.from(bndShp);
				return (await GeoData.toGeoJSON(shp, "SHP")) as unknown as
					| FeatureCollection<Polygon>
					| FeatureCollection<MultiPolygon>;
			}
		}
	}

	export async function getZones(job: Job) {
		if (job.attachments.zonesSHP && job.attachments.zonesSHP[0]) {
			const zonesShp = await FileDownloader.loadAttachment<ArrayBuffer>(
				job.attachments.zonesSHP[0],
				job.id,
				"arraybuffer",
			);
			if (zonesShp) {
				const shp = Buffer.from(zonesShp);
				return (await GeoData.toGeoJSON(shp, "SHP")) as unknown as
					| FeatureCollection<Polygon>
					| FeatureCollection<MultiPolygon>;
			}
		}
	}

	export async function getPoints(job: Job) {
		if (job.attachments.executedPointsSHP && job.attachments.executedPointsSHP[0]) {
			const exePtsShp = await FileDownloader.loadAttachment<ArrayBuffer>(
				job.attachments.executedPointsSHP[0],
				job.id,
				"arraybuffer",
			);
			if (exePtsShp) {
				const shp = Buffer.from(exePtsShp);
				return (await GeoData.toGeoJSON(shp, "SHP")) as unknown as FeatureCollection<Point>;
			}
		}

		if (job.attachments.pointsSHP && job.attachments.pointsSHP[0]) {
			const ptsShp = await FileDownloader.loadAttachment<ArrayBuffer>(
				job.attachments.pointsSHP[0],
				job.id,
				"arraybuffer",
			);
			if (ptsShp) {
				const shp = Buffer.from(ptsShp);
				return (await GeoData.toGeoJSON(shp, "SHP")) as unknown as FeatureCollection<Point>;
			}
		}
	}
}

export function getDealLabel(deal: Deal): string {
	const separator = " - ";
	const testPrefix = deal.testCompanyName ? `_TEST for ${deal.testCompanyName}${separator}` : "";
	const density = deal.density ? `${deal.density} acre` : "";
	const type = deal.type ? `${separator}${deal.type}` : "";
	const customizer = deal.customizer ? `${separator}${deal.customizer}` : "";
	const testPackage =
		deal.testPackage && deal.testPackage.toLowerCase() !== "none" ? `${separator}${deal.testPackage}` : "";
	const labName = deal.labName ? `${separator}${deal.labName}` : "";
	const depth = deal.depth ? `${separator}${deal.depth} in` : "";

	const label = `${testPrefix}${density}${type}${customizer}${testPackage}${labName}${depth}`;
	return label;
}
